import React, {useCallback, useContext, useRef, useState} from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {Button} from '@mui/material';
import {FileWithPath, useDropzone} from 'react-dropzone';
import {IFileUploaderProps} from '../../Interfaces/interfaces';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {uploadFileWithProgress, UploadHandle} from '../../../../services/uploadWithProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface FileUploaderProps extends IFileUploaderProps {
  onUploadStart?: (files: FileWithPath[]) => void;
  onUploadSuccess?: (files: FileWithPath[]) => void;
  onUploadError?: (files: FileWithPath[]) => void;
}

interface FileProgressInfo {
  percent: number;
  status: 'uploading' | 'processing' | 'done' | 'error';
  errorMessage?: string;
}

function getErrorMessage(error: {status: number; data?: any}): string {
  // Try to extract detail from FastAPI error response
  if (error.data?.detail) return error.data.detail;
  if (typeof error.data === 'string' && error.data) return error.data;

  switch (error.status) {
    case 409:
      return 'Datei existiert bereits in diesem Ordner.';
    case 413:
      return 'Datei ist zu groß.';
    case 500:
      return 'Serverfehler beim Hochladen.';
    case 0:
      return 'Netzwerkfehler — Verbindung zum Server fehlgeschlagen.';
    default:
      return `Fehler beim Hochladen (Status ${error.status}).`;
  }
}

function FileUploader(props: FileUploaderProps) {
  const {onClose, open, title, dir_id, onUploadStart, onUploadSuccess, onUploadError, ...other} =
    props;
  const {call_backend_api} = useContext(APIWrapperContext);
  const radioGroupRef = React.useRef<HTMLElement>(null);

  const [myFiles, setMyFiles] = useState<FileWithPath[]>([]);
  const [uploading, setUploading] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [fileProgress, setFileProgress] = useState<Map<string, FileProgressInfo>>(new Map());
  const abortHandlesRef = useRef<UploadHandle[]>([]);

  React.useEffect(() => {
    if (!open) {
      setUploading(false);
      setAllDone(false);
      setFileProgress(new Map());
      abortHandlesRef.current = [];
    }
  }, [open]);

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  const handleCancel = () => {
    if (uploading && !allDone) {
      // Abort all in-flight uploads
      abortHandlesRef.current.forEach((h) => h.abort());
      abortHandlesRef.current = [];
    }
    setUploading(false);
    setAllDone(false);
    setFileProgress(new Map());
    setMyFiles([]);
    onClose();
  };

  const hasErrors = () => {
    return Array.from(fileProgress.values()).some((p) => p.status === 'error');
  };

  const handleOk = () => {
    if (myFiles.length === 0) return;

    setUploading(true);
    setAllDone(false);
    onUploadStart?.(myFiles);

    const initialProgress = new Map<string, FileProgressInfo>();
    myFiles.forEach((f) => {
      initialProgress.set(f.name, {percent: 0, status: 'uploading'});
    });
    setFileProgress(new Map(initialProgress));

    let completedCount = 0;
    const totalFiles = myFiles.length;
    const handles: UploadHandle[] = [];

    myFiles.forEach((f: FileWithPath) => {
      const handle = uploadFileWithProgress(f, dir_id, (percent, phase) => {
        setFileProgress((prev) => {
          const next = new Map(prev);
          const current = next.get(f.name);
          if (current && (current.status === 'uploading' || current.status === 'processing')) {
            next.set(f.name, {
              ...current,
              percent,
              status: phase === 'processing' ? 'processing' : 'uploading',
            });
          }
          return next;
        });
      });

      handles.push(handle);

      handle.promise
        .then((response: any) => {
          console.log('File uploaded successfully:', response);
          setFileProgress((prev) => {
            const next = new Map(prev);
            next.set(f.name, {percent: 100, status: 'done'});
            return next;
          });
          onUploadSuccess?.([f]);
        })
        .catch((error: {status: number; data?: any}) => {
          const errorMessage = getErrorMessage(error);
          setFileProgress((prev) => {
            const next = new Map(prev);
            next.set(f.name, {
              percent: prev.get(f.name)?.percent ?? 0,
              status: 'error',
              errorMessage,
            });
            return next;
          });
          onUploadError?.([f]);
        })
        .finally(() => {
          completedCount++;
          if (completedCount === totalFiles) {
            setAllDone(true);
            // Only auto-close if everything succeeded
            setFileProgress((prev) => {
              const anyError = Array.from(prev.values()).some((p) => p.status === 'error');
              if (!anyError) {
                setTimeout(() => {
                  setMyFiles([]);
                  setUploading(false);
                  setAllDone(false);
                  setFileProgress(new Map());
                  abortHandlesRef.current = [];
                  onClose();
                }, 1000);
              }
              return prev;
            });
          }
        });
    });

    abortHandlesRef.current = handles;
  };

  const theme = useTheme();

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      setMyFiles([...myFiles, ...acceptedFiles]);
    },
    [myFiles]
  );

  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    disabled: uploading,
  });

  const removeFile = (file: FileWithPath) => () => {
    const newFiles = [...myFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setMyFiles(newFiles);
  };

  return (
    <Dialog
      sx={{'& .MuiDialog-paper': {width: '80%', maxHeight: 435}}}
      maxWidth='xs'
      TransitionProps={{onEntering: handleEntering}}
      open={open}
      onClick={(e) => {
        e.stopPropagation();
      }}
      {...other}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id='alert-dialog-description' component='div'>
          <section className='container'>
            {!uploading && (
              <Box
                sx={{border: '2px dotted ' + theme.palette.primary.dark, p: 1}}
                {...getRootProps({className: 'dropzone'})}>
                <input {...getInputProps()} />
                <p>Dateien hier per Drag 'n' drop hochladen, oder Dateien per Click selektieren</p>
              </Box>
            )}
            <Box>
              <h4>{uploading ? 'Upload Fortschritt' : 'Dateien zum hochladen'}</h4>
              {uploading
                ? myFiles.map((file) => {
                    const progress = fileProgress.get(file.name);
                    const isProcessing = progress?.status === 'processing';
                    return (
                      <Box key={file.name} sx={{mb: 1.5}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                          {progress?.status === 'done' && (
                            <CheckCircleIcon color='success' fontSize='small' />
                          )}
                          {progress?.status === 'error' && (
                            <ErrorIcon color='error' fontSize='small' />
                          )}
                          <Typography variant='body2' noWrap sx={{flex: 1}}>
                            {file.name}
                          </Typography>
                          <Typography variant='body2' color='text.secondary' sx={{whiteSpace: 'nowrap'}}>
                            {isProcessing
                              ? 'Verarbeitung...'
                              : progress?.status === 'error'
                              ? 'Fehler'
                              : `${progress?.percent ?? 0}%`}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant={isProcessing ? 'indeterminate' : 'determinate'}
                          value={isProcessing ? undefined : (progress?.percent ?? 0)}
                          color={
                            progress?.status === 'error'
                              ? 'error'
                              : progress?.status === 'done'
                              ? 'success'
                              : 'primary'
                          }
                        />
                        {progress?.status === 'error' && progress.errorMessage && (
                          <Typography variant='caption' color='error' sx={{mt: 0.5, display: 'block'}}>
                            {progress.errorMessage}
                          </Typography>
                        )}
                      </Box>
                    );
                  })
                : myFiles.map((file) => (
                    <Chip
                      key={file.name}
                      sx={{m: 0.2}}
                      label={file.path + ' - ' + (file.size / 1024 / 1024).toFixed(2) + ' MB'}
                      onDelete={removeFile(file)}
                    />
                  ))}
            </Box>
          </section>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {uploading && allDone && hasErrors() ? (
          <Button onClick={handleCancel}>Schließen</Button>
        ) : (
          <>
            <Button onClick={handleCancel}>{uploading ? 'Abbrechen' : 'Schließen'}</Button>
            {!uploading && (
              <Button onClick={handleOk} autoFocus disabled={myFiles.length === 0}>
                Hochladen
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
export default FileUploader;
